import {
  Button,
  CircularProgress,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { createStyles, makeStyles } from '@material-ui/styles';
import cloneDeep from 'lodash.clonedeep';
import React, { useEffect, useState, useMemo } from 'react';
import { Entity, EntityClass, EntityClassProperty } from '../../../../../types';
import { JupiterTheme } from '../../../../theme/types';
import CustomPropertiesForm from './CustomPropertiesForm';
import EntityDefinition from './EntityDefinition';
import { formatValue } from './formatValue';
import PropertiesForm from './PropertiesForm';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    saveButton: {
      marginTop: theme.spacing(4),
      marginLeft: 'auto',
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
    },
  })
);

interface HookProps {
  selectedClass: any;
  entity?: Entity;
  onSaveEntity: (entity: Entity) => void;
}
interface Props extends HookProps {
  busy: boolean;
  selectedClassName: string;
  isIntegrationEntity: boolean;
}

const DEFAULT_ENTITY = {
  entityKey: '',
  entityType: [''],
  entityClass: undefined,
  properties: {} as any,
};

export const useCreateEntityForm = ({
  selectedClass,
  entity: baseEntity,
  onSaveEntity,
}: HookProps) => {
  const [entity, setEntity] = useState<Entity>(DEFAULT_ENTITY);
  const canSave = useMemo(() => {
    return !!(entity.entityType[0] && entity.properties.displayName);
  }, [entity]);

  useEffect(() => {
    if (!baseEntity) return setEntity(DEFAULT_ENTITY);
    return setEntity(cloneDeep(baseEntity));
  }, [baseEntity]);

  const handleUpdateProperties = (key: string, value: any) => {
    const newEntity = cloneDeep(entity);
    newEntity.properties[key] = formatValue(value);
    if (value === null || value === '') {
      delete newEntity.properties[key];
    }
    setEntity(newEntity);
  };
  const handleUpdateManyProperties = (keys: string[], value: any) => {
    const newEntity = cloneDeep(entity);
    keys.forEach((key) => {
      newEntity.properties[key] = formatValue(value);
      if (value === null || value === '') {
        delete newEntity.properties[key];
      }
    });
    setEntity(newEntity);
  };

  const handleUpdateEntity = (key: string, propertyKey: string, value: any) => {
    const newEntity = cloneDeep(entity);
    (newEntity as any)[key] = value;
    if (propertyKey) {
      newEntity.properties[key] = value;
    }
    setEntity(newEntity);
  };

  const handleAddEntity = () => {
    Object.keys(baseEntity ? baseEntity.properties : {}).forEach((key) => {
      // if the property got deleted set it to null so that the update API will
      // persisted the deleted property.
      if (entity.properties[key] === undefined) {
        entity.properties[key] = null;
      }
    });
    onSaveEntity(entity);
  };

  const getClassProperties = () => {
    const properties = (selectedClass as EntityClass).properties;
    const forbiddenTags = [
      'tag.*',
      'tags',
      'name',
      'displayName',
      'summary',
      'description',
      'classification',
    ];
    return properties
      .filter((p: EntityClassProperty) => {
        return (
          forbiddenTags.indexOf(p.name) === -1 && p.source !== 'GraphObject'
        );
      })
      .sort((p: EntityClassProperty) => {
        return p.required ? -1 : 1;
      });
  };
  return {
    entity,
    canSave,
    handleUpdateEntity,
    handleUpdateProperties,
    handleUpdateManyProperties,
    handleAddEntity,
    getClassProperties,
  };
};

const CreateEntityForm = ({
  busy,
  selectedClass,
  selectedClassName,
  isIntegrationEntity,
  ...rest
}: Props) => {
  const classes = useStyles();

  const {
    entity,
    canSave,
    handleUpdateEntity,
    handleUpdateProperties,
    handleUpdateManyProperties,
    handleAddEntity,
    getClassProperties,
  } = useCreateEntityForm({ ...rest, selectedClass });
  return (
    <div className={classes.container}>
      <EntityDefinition
        onUpdateProperties={handleUpdateProperties}
        onUpdateManyProperties={handleUpdateManyProperties}
        onUpdateEntity={handleUpdateEntity}
        entity={entity}
        selectedClass={selectedClass}
        selectedClassName={selectedClassName}
        isIntegrationEntity={isIntegrationEntity}
      />
      <ExpansionPanel>
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
          Defined Properties
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          {
            <PropertiesForm
              entity={entity}
              onUpdateProperties={handleUpdateProperties}
              properties={getClassProperties()}
            />
          }
        </ExpansionPanelDetails>
      </ExpansionPanel>
      <ExpansionPanel disabled={selectedClass === null}>
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
          Custom Properties
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <CustomPropertiesForm
            onUpdateProperties={handleUpdateProperties}
            entity={entity}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>
      <ExpansionPanel disabled={selectedClass === null}>
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
          Tags
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <CustomPropertiesForm
            tagsForm={true}
            onUpdateProperties={handleUpdateProperties}
            entity={entity}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>
      <Button
        className={classes.saveButton}
        variant="contained"
        color="primary"
        onClick={handleAddEntity}
        disabled={busy || !canSave}
      >
        {busy ? <CircularProgress /> : 'Save'}
      </Button>
    </div>
  );
};

export default CreateEntityForm;
